import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export interface UserAppTag {
  app_count: number
  id: string
  name: string
  normalized_name: string
}

export interface UserApp {
  app_icon?: string | null
  app_type: string
  avatar?: string | null
  description: string
  distribution_mode?: 'automatic' | 'manual'
  id: string
  is_favorite: boolean
  is_installed: boolean
  is_preinstalled?: boolean
  name: string
  status: string
  tags?: { id: string; name: string }[]
  usage_instructions?: string | null
  version?: string | null
}

export interface UserAppsListParams {
  page?: number
  pageSize?: number
  search?: string
  tagId?: string
}

export interface UserAppsListData {
  items: UserApp[]
  page: number | null
  page_size: number | null
  total: number
}

export type UserAppTagCollection = 'installed' | 'market'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('user'),
  portal: 'user',
})

function listQuery(params: UserAppsListParams) {
  const query = new URLSearchParams()
  query.set('page', String(params.page ?? 1))
  query.set('page_size', String(params.pageSize ?? 100))

  const search = params.search?.trim()
  if (search) {
    query.set('search', search)
  }

  if (params.tagId) {
    query.set('tag_id', params.tagId)
  }

  return query.toString()
}

async function listUserApps(path: string, params: UserAppsListParams) {
  const query = listQuery(params)
  const response = await apiClient.request<SuccessEnvelope<UserAppsListData>>(`${path}?${query}`)

  return response.data
}

/** 获取应用导航使用的完整已安装应用集合，不携带分页或标签参数。 */
export async function listInstalledAppsForNavigation(params: { search?: string } = {}) {
  const query = new URLSearchParams()
  const search = params.search?.trim()

  if (search) {
    query.set('search', search)
  }

  const queryString = query.toString()
  const response = await apiClient.request<SuccessEnvelope<UserAppsListData>>(
    `/app/my-apps${queryString ? `?${queryString}` : ''}`,
  )

  return response.data
}

export function listMarketApps(params: UserAppsListParams) {
  return listUserApps('/app/market-apps', params)
}

export function listMyApps(params: UserAppsListParams) {
  return listUserApps('/app/my-apps', params)
}

export async function listUserAppTags(collection: UserAppTagCollection) {
  const query = new URLSearchParams({ collection })
  const response = await apiClient.request<SuccessEnvelope<{ items: UserAppTag[] }>>(
    `/app/tags?${query.toString()}`,
  )

  return response.data.items
}

export async function installUserApp(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<{ id: string }>>(
    `/app/my-apps/${encodeURIComponent(appId)}`,
    {
      method: 'POST',
    },
  )

  return response.data
}

export async function uninstallUserApp(appId: string) {
  await apiClient.request(`/app/my-apps/${encodeURIComponent(appId)}`, {
    method: 'DELETE',
  })
}

export async function favoriteUserApp(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<{ id: string }>>(
    `/app/favorites/${encodeURIComponent(appId)}`,
    {
      method: 'POST',
    },
  )

  return response.data
}

export async function unfavoriteUserApp(appId: string) {
  await apiClient.request(`/app/favorites/${encodeURIComponent(appId)}`, {
    method: 'DELETE',
  })
}
